import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomDialog } from '../context/DialogContext';
import usePWAInstall from '../hooks/usePWAInstall';

const DeliveryDashboard = () => {
    const [token, setToken] = useState(null);
    const [user, setUser] = useState(null);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const { alert: customAlert, confirm: customConfirm } = useCustomDialog();
    usePWAInstall();

    useEffect(() => {
        const storedToken = localStorage.getItem('staff_token');
        const storedUser = localStorage.getItem('staff_user');
        if (storedToken && storedUser) {
            const parsedUser = JSON.parse(storedUser);
            if (parsedUser.role !== 'delivery') {
                navigate('/kitchen-dashboard');
            } else {
                setToken(storedToken);
                setUser(parsedUser);
            }
        } else {
            navigate('/staff-login');
        }
    }, [navigate]);

    const handleLogout = useCallback(() => {
        localStorage.removeItem('staff_token');
        localStorage.removeItem('staff_user');
        navigate('/staff-login');
    }, [navigate]);

    const fetchOrders = useCallback(async () => {
        if (!token) return;
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_billing.php?action=fetch_orders', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    order_type: 'Delivery'
                })
            });
            
            if (res.status === 401) {
                handleLogout();
                return;
            }
            
            const json = await res.json();
            if (json.success) {
                // Only show orders that are Ready from kitchen, and not yet fully Delivered
                const activeDeliveries = json.orders.filter(o => 
                    o.kitchen_status === 'Ready' && 
                    (!o.delivery_status || o.delivery_status !== 'Delivered')
                );
                
                // Play sound if new orders arrived
                if (activeDeliveries.length > orders.length && orders.length > 0) {
                    const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
                    audio.play().catch(e => console.log("Sound play blocked"));
                }
                
                setOrders(activeDeliveries);
            }
        } catch (err) {
            console.error("Fetch Delivery Orders Error:", err);
        } finally {
            setLoading(false);
        }
    }, [token, orders.length, handleLogout]);

    useEffect(() => {
        if (token) {
            fetchOrders();
            const interval = setInterval(fetchOrders, 30000); // 30s auto-refresh
            return () => clearInterval(interval);
        }
    }, [token, fetchOrders]);

    const updateDeliveryStatus = async (orderId, newStatus, pm = null) => {
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_billing.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    action: 'update_delivery_status',
                    order_id: orderId,
                    delivery_status: newStatus,
                    payment_mode: pm
                })
            });
            const json = await res.json();
            if (json.success) {
                fetchOrders();
            } else {
                customAlert(json.message);
            }
        } catch (err) {
            customAlert("Failed to update status.");
        }
    };

    const handleMarkDelivered = async (order) => {
        if (order.order_status === 'Paid') {
            // Already paid, just mark delivered
            const confirmed = await customConfirm("Mark this order as Delivered?", "Confirm Delivery", "success");
            if (confirmed) {
                updateDeliveryStatus(order.id, 'Delivered');
            }
        } else {
            // Needs payment collection
            const isCash = await customConfirm("Did you collect payment via CASH?", "Payment Method", "info");
            if (isCash) {
                updateDeliveryStatus(order.id, 'Delivered', 'Cash');
            } else {
                const isUpi = await customConfirm("Did you collect payment via UPI?", "Payment Method", "info");
                if (isUpi) {
                    updateDeliveryStatus(order.id, 'Delivered', 'UPI');
                }
            }
        }
    };

    if (!token) return null;

    return (
        <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'Outfit', sans-serif" }}>
            <header style={{ background: '#fff', padding: '15px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e2e8f0', position: 'sticky', top: 0, zIndex: 100 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ width: '40px', height: '40px', background: '#4338ca', color: '#fff', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <i className="fas fa-motorcycle" />
                    </div>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '800' }}>Delivery Board</h2>
                        <p style={{ margin: 0, fontSize: '0.8rem', color: '#64748b' }}>Rider: {user?.name}</p>
                    </div>
                </div>
                <button 
                    onClick={handleLogout}
                    style={{ background: '#fee2e2', color: '#ef4444', border: 'none', padding: '8px 16px', borderRadius: '50px', fontWeight: '700', fontSize: '0.85rem', cursor: 'pointer' }}
                >
                    <i className="fas fa-sign-out-alt" />
                </button>
            </header>

            <main style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ margin: 0, color: '#1e293b' }}>Active Deliveries ({orders.length})</h3>
                    <button onClick={fetchOrders} style={{ background: '#e2e8f0', border: 'none', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', color: '#475569' }}>
                        <i className="fas fa-sync-alt" />
                    </button>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading active deliveries...</div>
                ) : orders.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '50px', background: '#fff', borderRadius: '16px', border: '2px dashed #cbd5e1' }}>
                        <i className="fas fa-box-open" style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '15px' }}></i>
                        <h3 style={{ margin: 0, color: '#64748b' }}>No pending deliveries</h3>
                        <p style={{ fontSize: '0.9rem', color: '#94a3b8' }}>Waiting for the kitchen to prepare orders...</p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        {orders.map(order => (
                            <div key={order.id} style={{ background: '#fff', borderRadius: '16px', padding: '20px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '15px', paddingBottom: '15px', borderBottom: '1px dashed #e2e8f0' }}>
                                    <div>
                                        <h4 style={{ margin: '0 0 5px 0', fontSize: '1.2rem', color: '#1e293b' }}>#{order.order_no}</h4>
                                        <span style={{ 
                                            padding: '4px 10px', 
                                            borderRadius: '50px', 
                                            fontSize: '0.75rem', 
                                            fontWeight: '700', 
                                            background: order.delivery_status === 'Out for Delivery' ? '#fef3c7' : '#e0e7ff',
                                            color: order.delivery_status === 'Out for Delivery' ? '#b45309' : '#4338ca'
                                        }}>
                                            {order.delivery_status || 'Pending Dispatch'}
                                        </span>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: '1.3rem', fontWeight: '800', color: '#10b981' }}>
                                            ₹{order.order_status === 'Advance Paid' ? (parseFloat(order.grand_total) - parseFloat(order.advance_paid || 0)).toFixed(2) : order.grand_total}
                                        </div>
                                        <span style={{ fontSize: '0.8rem', color: order.order_status === 'Paid' ? '#10b981' : '#ef4444', fontWeight: '700' }}>
                                            {order.order_status === 'Paid' ? 'PAID' : (order.order_status === 'Advance Paid' ? 'COLLECT BALANCE' : 'COLLECT CASH')}
                                        </span>
                                    </div>
                                </div>
                                
                                <div style={{ marginBottom: '20px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                                        <div style={{ width: '30px', height: '30px', background: '#f1f5f9', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
                                            <i className="fas fa-user"></i>
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: '600', color: '#1e293b' }}>{order.customer_name || 'Guest'}</div>
                                            <a href={`tel:${order.customer_phone}`} style={{ fontSize: '0.85rem', color: 'var(--primary-color)', textDecoration: 'none', fontWeight: '600' }}>
                                                <i className="fas fa-phone-alt" style={{ marginRight: '5px' }}></i> {order.customer_phone || 'N/A'}
                                            </a>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                        <div style={{ width: '30px', height: '30px', background: '#f1f5f9', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', flexShrink: 0 }}>
                                            <i className="fas fa-map-marker-alt"></i>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: '0.9rem', color: '#475569', lineHeight: '1.4' }}>
                                                {order.customer_address || 'No address provided'}
                                            </div>
                                            {order.customer_address && (
                                                <a 
                                                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(order.customer_address)}`} 
                                                    target="_blank" 
                                                    rel="noreferrer"
                                                    style={{ display: 'inline-block', marginTop: '5px', fontSize: '0.8rem', color: 'var(--primary-color)', textDecoration: 'none', fontWeight: '600' }}
                                                >
                                                    <i className="fas fa-directions"></i> Open in Maps
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div style={{ display: 'flex', gap: '10px' }}>
                                    {(!order.delivery_status || order.delivery_status === 'Pending') && (
                                        <button 
                                            onClick={() => updateDeliveryStatus(order.id, 'Out for Delivery')}
                                            style={{ flex: 1, padding: '12px', background: '#fef3c7', color: '#b45309', border: 'none', borderRadius: '10px', fontWeight: '700', cursor: 'pointer' }}
                                        >
                                            Start Delivery
                                        </button>
                                    )}
                                    <button 
                                        onClick={() => handleMarkDelivered(order)}
                                        style={{ flex: 1, padding: '12px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '10px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                                    >
                                        <i className="fas fa-check-circle"></i> Mark Delivered
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
};

export default DeliveryDashboard;
